import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { equals } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { FilterListOffRounded, FilterListRounded } from '@mui/icons-material'
import { Box, Button } from '@mui/material'
import { DataGridPro, GridFooter, useGridApiRef } from '@mui/x-data-grid-pro'

import { SearchBox } from '@common/components'
import { noop, useLocalStorage } from '@common/utils'

import Filter from './Filter'
import ListExportButton from './ListExportButton'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object[]} props.columns
 * @param {string} props.columns[].field
 * @param {string} props.columns[].headerName
 * @param {number} props.columns[].width
 * @param {number} props.page
 * @param {Object[]} props.rows
 * @param {number} props.rowCount
 * @param {Function} [props.cellClick]
 * @param {boolean} [props.loading]
 * @param {boolean} [props.queryDrivenSearch]
 * @param {Function} [props.setSearch]
 * @param {boolean} [props.checkbox]
 * @param {number} [props.pageSize]
 * @param {Function} [props.pageChange]
 * @param {Function} [props.pageSizeChange]
 * @param {Function} [props.rowClick]
 * @param {Function} [props.sortChange]
 * @param {string} [props.noValuePlaceholder]
 * @param {boolean} [props.columnsAutosize]
 * @param {boolean} [props.enableTopScrollbar]
 * @param {boolean} [props.enableTopPagination]
 * @param {boolean} [props.hideFooter]
 * @param {Object} [props.filterProps]
 * @param {Object} props.filterProps.apiParams
 * @param {Function} props.filterProps.setApiParams
 * @param {React.Node[]} props.filterProps.entries
 * @param {boolean} [props.filterProps.isGlobalState]
 * @param {Object} [props.exportListConfig]
 * @param {Object} [props.exportListConfig.apiParams]
 * @param {string} [props.exportListConfig.entity]
 * @param {React.Node} [props.extraHeaderContent]
 * @param {React.Element} [props.customAddButton]
 * @param {number} [props.searchTriggerLength]
 * @param {Object} [props.slots]
 * @param {Object} [props.sx]
 */
export default function List({
  id,
  title,
  cellClick = noop,
  checkbox = false,
  loading = false,
  queryDrivenSearch = false,
  setSearch = null,
  columns,
  page,
  pageChange = noop,
  pageSize = 25,
  pageSizeChange = noop,
  rows,
  rowCount,
  rowClick = noop,
  sortChange = noop,
  noValuePlaceholder = '',
  enableTopScrollbar = false,
  enableTopPagination = true,
  hideFooter = false,
  filterProps = undefined,
  exportListConfig = undefined,
  extraHeaderContent = undefined,
  customActions = undefined,
  searchTriggerLength = 3,
  slots = {},
  sx = {},
  ...dataGridProps
}) {
  const columnsAutosize = false // temporary disable autosizing feature

  const parentRef = useRef(null)
  const apiRef = useGridApiRef()

  const { doUpdateQuery, queryObject } = useConnect(
    'doUpdateQuery',
    'selectQueryObject',
  )

  const [expandedRowsIds, setExpandedRowsIds] = useState(new Set())
  const [internalRows, setInternalRows] = useState(rows ?? [])
  const [columnsSizes, setColumnsSizes] = useState(null)
  const [listFilterState, setListFilterState] = useLocalStorage(
    'portalListFilterState',
    {},
  )

  const autosizeOptions = useMemo(
    () => ({
      includeHeaders: true,
      includeOutliers: true,
      expand: true,
      columns: columns.map((colDef) => colDef.field),
    }),
    [columns],
  )

  useEffect(() => {
    if (!equals(rows, internalRows)) {
      setInternalRows(rows)
    }
  }, [rows])

  useEffect(() => {
    if (columnsAutosize && columnsSizes) {
      queueMicrotask(() => {
        apiRef.current.autosizeColumns(autosizeOptions)
      })
    }
  }, [autosizeOptions, columnsSizes, columnsAutosize])

  useEffect(() => {
    if (!enableTopScrollbar) {
      return () => {}
    }

    const scroller = parentRef?.current?.querySelector(
      `.MuiDataGrid-scrollbar--horizontal:not(.duplicated)`,
    )
    const targetContainer = parentRef.current?.querySelector('.top-scrollbar-container')
    const duplicatedTopScroller = scroller?.cloneNode(true)

    if (targetContainer?.childElementCount > 0) {
      targetContainer.innerHTML = ''
    }

    if (duplicatedTopScroller) {
      duplicatedTopScroller.style.top = 0
      duplicatedTopScroller.className += ' duplicated'
      targetContainer.style.height = '16px'
      targetContainer.appendChild(duplicatedTopScroller)
    }

    const syncScroll = () => {
      if (duplicatedTopScroller) {
        duplicatedTopScroller.scrollLeft = scroller.scrollLeft
      }
    }
    const syncScrollTop = () => {
      if (scroller) {
        scroller.scrollLeft = duplicatedTopScroller.scrollLeft
      }
    }

    scroller?.addEventListener('scroll', syncScroll)
    duplicatedTopScroller?.addEventListener('scroll', syncScrollTop)

    return () => {
      scroller?.removeEventListener('scroll', syncScroll)
      duplicatedTopScroller?.removeEventListener('scroll', syncScrollTop)
      if (targetContainer) {
        targetContainer.innerHTML = ''
      }
    }
  })

  const updateRows = useCallback(async () => {
    if (columnsAutosize) {
      if (
        !apiRef.current ||
        autosizeOptions.columns.some(
          (field) => !apiRef.current.state.columns.lookup[field],
        )
      ) {
        return
      }

      queueMicrotask(async () => {
        await apiRef.current.autosizeColumns(autosizeOptions)

        const columnsData = apiRef.current.state.columns.lookup
        const updatedSized = Object.keys(columnsData)
          .map((key) => {
            const colDef = columnsData[key]
            const width = colDef.computedWidth
            return { [key]: { width, hasBeenResized: colDef.hasBeenResized } }
          })
          .reduce((acc, item) => ({ ...acc, ...item }), {})
        setColumnsSizes(updatedSized)
      })
    }
  }, [autosizeOptions, internalRows, columnsAutosize])

  useEffect(() => {
    updateRows()
  }, [internalRows, autosizeOptions])

  let resizeTimer
  const handleWindowResize = () => {
    if (columnsAutosize) {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        updateRows()
      }, 250)
    }
  }

  const componentRef = useCallback(
    (node) => {
      if (!node || !columnsAutosize) return
      const resizeObserver = new ResizeObserver(() => {
        handleWindowResize()
      })
      resizeObserver.observe(node)
    },
    [autosizeOptions, columnsAutosize],
  )

  const handleSortChange = (sort) => {
    if (!sort.length) return sortChange(sort)
    const column = columns.find((col) => col.field === sort[0].field)
    const field = column.sortField ?? column.field
    return sortChange([field, sort[0].sort])
  }

  const onPaginationModelChange = (model) => {
    if (model.page !== page - 1) {
      pageChange(model.page + 1)
    }
    if (model.pageSize !== pageSize) {
      pageSizeChange(model.pageSize)
    }
  }

  const queryDrivenSearchHandler = (search) => {
    const queryParams = { ...queryObject, page: 1, search }
    if (!queryParams.search) {
      delete queryParams.search
    }
    doUpdateQuery(queryParams)
  }

  const modifiedColumns = useMemo(
    () =>
      columns.map((column) => {
        let updateColDef = {
          display: column.renderCell ? 'flex' : 'block',
          valueGetter: (value) =>
            value === null || value === undefined || value === ''
              ? noValuePlaceholder
              : value,
          ...column,
        }
        if (columnsAutosize) {
          delete updateColDef.flex
          updateColDef = {
            ...updateColDef,
            width: columnsSizes?.[column.field]?.width ?? updateColDef.width,
          }
        }
        return updateColDef
      }),
    [noValuePlaceholder, columnsAutosize, columnsSizes],
  )

  const showListFilter =
    filterProps &&
    filterProps.apiParams &&
    filterProps.setApiParams &&
    filterProps.entries

  const {
    entries: filterEntries,
    isGlobalState = true,
    ...restFilterProps
  } = filterProps ?? {}

  const filterStateId = isGlobalState ? 'global' : id

  const paginationToolbarVisible = !hideFooter && enableTopPagination

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          justifyItems="center"
          alignContent="center"
        >
          {showListFilter && (
            <Button
              variant="outlined"
              startIcon={
                listFilterState[filterStateId] ? (
                  <FilterListOffRounded />
                ) : (
                  <FilterListRounded />
                )
              }
              onClick={() =>
                setListFilterState((prevState) => ({
                  ...prevState,
                  [filterStateId]: !prevState[filterStateId],
                }))
              }
              sx={{ mr: 1.5, height: 40 }}
            >
              {listFilterState[filterStateId] ? 'Hide' : 'Show'} Filters
            </Button>
          )}
          {setSearch || queryDrivenSearch ? (
            <SearchBox
              title={title}
              margin="dense"
              minLength={searchTriggerLength}
              onSetSearch={queryDrivenSearch ? queryDrivenSearchHandler : setSearch}
              sx={{ mt: 1, mb: 1 }}
            />
          ) : null}
          {extraHeaderContent}
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {exportListConfig && <ListExportButton {...exportListConfig} />}
          {customActions}
        </Box>
      </Box>
      <Box display="flex">
        {showListFilter && (
          <Filter
            id={id}
            mode="drawer"
            externalState={[listFilterState, setListFilterState]}
            paperSx={{ mt: enableTopPagination ? 8 : 2, paddingRight: 4 }}
            {...restFilterProps}
          >
            {filterEntries}
          </Filter>
        )}
        <Box
          key={`grid_${id}_filter_${listFilterState[filterStateId]}`}
          ref={parentRef}
          sx={{ position: 'relative', width: 1, overflow: 'hidden' }}
        >
          <Box className="top-scrollbar-container" position="relative" />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              '& .MuiDataGrid-container--top:after': { height: 0 },
              '& .MuiDataGrid-root, .MuiDataGrid-footerContainer, .MuiDataGrid-columnHeaders':
                { border: 'none' },
            }}
          >
            <DataGridPro
              pagination
              disableColumnFilter
              disableColumnMenu
              disableColumnReorder
              disableColumnPinning
              disableRowSelectionOnClick
              apiRef={apiRef}
              ref={componentRef}
              checkboxSelection={checkbox}
              columns={modifiedColumns}
              density="comfortable"
              loading={loading}
              paginationModel={{ page: page - 1, pageSize }}
              onPaginationModelChange={onPaginationModelChange}
              paginationMode="server"
              resizeThrottleMs={0}
              onCellClick={cellClick}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              rows={rows}
              rowCount={rowCount}
              pageSizeOptions={[10, 25, 50, 100]}
              hideFooter={hideFooter}
              showToolbar={paginationToolbarVisible}
              onRowClick={rowClick}
              detailPanelExpandedRowIds={expandedRowsIds}
              onDetailPanelExpandedRowIdsChange={setExpandedRowsIds}
              slots={{
                ...slots,
                toolbar: paginationToolbarVisible ? GridFooter : undefined,
              }}
              sx={{
                '& .MuiDataGrid-columnSeparator': { display: 'none' },
                '& .MuiDataGrid-cell': { alignContent: 'center' },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                '& .clickableCell': (theme) => ({
                  cursor: 'pointer',
                  color: 'primary.main',
                  ...theme.typography.subtitle2,
                }),
                ...sx,
              }}
              {...dataGridProps}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
